import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Chip,
  Stack,
  AccordionGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  Modal,
  ModalClose,
  Sheet,
} from "@mui/joy";
import {
  EditNote as EditNoteIcon,
  Forum as ForumIcon,
  PsychologyAlt as PsychologyAltIcon,
} from "@mui/icons-material";
import { useParams, useLocation } from "react-router-dom";
import JotFormElement from "../components/JotFormElement";
import apiService from "../services/api";
import type {
  CandidateSearchResult,
  JotformSubmission,
  PC101Scores,
  KaitomData,
} from "../types";
import { useAuth } from "../contexts/AuthContext";

const CandidateDetailPage: React.FC = () => {
  const { citizenId } = useParams<{ citizenId: string }>();
  const location = useLocation();
  const [candidateData, setCandidateData] =
    useState<CandidateSearchResult | null>(
      location.state?.candidateData || null
    );
  const [loading, setLoading] = useState(!candidateData);
  const [error, setError] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!candidateData && citizenId) {
      loadCandidateData();
    }
  }, [citizenId, candidateData]);

  const loadCandidateData = async () => {
    if (!citizenId) return;

    try {
      setLoading(true);
      const response = await apiService.searchCandidate(citizenId);
      if (response.success) {
        setCandidateData(response.data);
      } else {
        setError(response.error || "ไม่พบข้อมูลผู้สมัคร");
      }
    } catch (error: any) {
      console.error("Load candidate error:", error);
      setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const getPersonalInfo = () => {
    const answers =
      candidateData?.mp101[0]?.answers ||
      candidateData?.mp101partylist[0]?.answers;
    if (!answers) return null;
    const citizenIdField = answers["294"] || answers["key"];
    const nameField = answers["233"];
    const phoneField = answers["37"];
    const emailField = answers["38"];
    const birthDateField = answers["254"];
    const sexField = answers["236"];
    const genderField = answers["333"];
    const imageField = answers["41"];
    let imageUrl = null;
    if (imageField?.answer) {
      const parsedAnswer = JSON.parse(imageField.answer);
      if (
        parsedAnswer?.widget_metadata?.type === "imagelinks" &&
        parsedAnswer?.widget_metadata?.value.length > 0
      ) {
        imageUrl = `https://www.jotform.com${parsedAnswer?.widget_metadata?.value[0]?.url}`;
      }
    }

    return {
      citizenId: citizenIdField?.answer
        ? citizenIdField?.answer.replace(/-/g, "")
        : "",
      name:
        nameField?.prettyFormat ||
        (nameField?.answer
          ? `${nameField.answer.first || ""} ${
              nameField.answer.last || ""
            }`.trim()
          : ""),
      firstName: nameField?.answer?.first || "",
      lastName: nameField?.answer?.last || "",
      phone: phoneField?.answer
        ? typeof phoneField.answer === "object"
          ? phoneField.answer.full
          : phoneField.answer
        : "",
      email: emailField?.answer || "",
      birthDate: birthDateField?.prettyFormat || "",
      sex: sexField?.answer || "",
      gender: genderField?.answer || "",
      imageUrl,
    };
  };

  const renderPC101Scores = (pc101Scores: PC101Scores) => {
    if (!pc101Scores.data || pc101Scores.data.length === 0) return null;
    return (
      <Grid container spacing={2}>
        {pc101Scores.data.map((data) => {
          return (
            <>
              <Grid xs={12} md={6}>
                <Typography level="body-md" sx={{ fontWeight: "bold" }}>
                  {data.key}
                </Typography>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="body-md">{data.value}</Typography>
              </Grid>
            </>
          );
        })}
      </Grid>
    );
  };

  const renderFormElements = (mp101: JotformSubmission) => {
    if (!mp101.answers) return null;

    const answers = mp101.answers;

    // Sort elements by order
    const sortedElements = Object.entries(answers)
      .sort(([, a], [, b]) => parseInt(a.order) - parseInt(b.order))
      .map(([key, element]) => ({ key, ...element }));

    return sortedElements.map((element) => (
      <JotFormElement key={element.key} element={element} />
    ));
  };

  const renderKaitomData = (kaitomData: KaitomData[]) => {
    return (
      <Grid container spacing={2}>
        {kaitomData.map((item, index) => {
          let tags: string[] = [];
          try {
            if (item.tags) {
              tags = JSON.parse(item.tags);
            }
          } catch (e) {
            console.error("Failed to parse tags:", e);
          }

          let images: string[] = [];
          try {
            if (item.images) {
              images = JSON.parse(item.images);
            }
          } catch (e) {
            console.error("Failed to parse images:", e);
          }

          return (
            <Grid xs={12} key={index}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  {item.location_name || "ไม่มีชื่อสถานที่"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={12} md={6}>
                    <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                      วันที่:
                    </Typography>
                    <Typography level="body-md">
                      {new Date(item.date).toLocaleDateString("th-TH", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                      สถานที่:
                    </Typography>
                    <Typography level="body-md">
                      {item.subdistrict && `ต.${item.subdistrict} `}
                      {item.district && `อ.${item.district} `}
                      {item.province && `จ.${item.province}`}
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                      พิกัด:
                    </Typography>
                    <Typography level="body-md">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.latitude?.toFixed(6)},{" "}
                        {item.longitude?.toFixed(6)}
                      </a>
                    </Typography>
                  </Grid>
                  {item.description && (
                    <Grid xs={12} md={6}>
                      <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                        รายละเอียด:
                      </Typography>
                      <Typography level="body-md">
                        {item.description}
                      </Typography>
                    </Grid>
                  )}
                  {tags.length > 0 && (
                    <Grid xs={12} md={6}>
                      <Typography
                        level="body-sm"
                        sx={{ fontWeight: "medium", mb: 1 }}
                      >
                        แท็ก:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            variant="soft"
                            color="primary"
                            size="sm"
                          >
                            {tag}
                          </Chip>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                  {images.length > 0 && (
                    <Grid xs={12}>
                      <Typography
                        level="body-sm"
                        sx={{ fontWeight: "medium", mb: 1 }}
                      >
                        รูปภาพ:
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        {images.map((image, idx) => (
                          <Box
                            key={idx}
                            onClick={() =>
                              setSelectedImage(
                                `https://img.pplethai.org/unsafe/rs:fit:1000:1000:1/plain/${encodeURIComponent(
                                  `https://storage.googleapis.com/mp-assistant-bot/${image}`
                                )}`
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <img
                              src={`https://img.pplethai.org/unsafe/rs:fit:1000:1000:1/plain/${encodeURIComponent(
                                `https://storage.googleapis.com/mp-assistant-bot/${image}`
                              )}`}
                              alt={`รูปภาพที่ ${idx + 1}`}
                              style={{
                                maxWidth: "200px",
                                maxHeight: "200px",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                  <Grid xs={12}>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      บันทึกเมื่อ:{" "}
                      {new Date(item.created_at).toLocaleString("th-TH", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography level="body-md">กำลังโหลดข้อมูล...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!candidateData) {
    return (
      <Alert color="warning" sx={{ mb: 2 }}>
        ไม่พบข้อมูลผู้สมัคร
      </Alert>
    );
  }

  const personalInfo = getPersonalInfo();

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 3 }}>
        รายละเอียดผู้สมัคร MP101
      </Typography>

      {/* Image Modal */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          variant="outlined"
          sx={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: "md",
            p: 3,
            boxShadow: "lg",
            position: "relative",
          }}
        >
          <ModalClose
            variant="plain"
            sx={{ zIndex: 10000, backgroundColor: "white" }}
          />
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size"
              style={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          )}
        </Sheet>
      </Modal>

      <AccordionGroup>
        {/* Personal Information Summary */}
        {personalInfo && (
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography level="h3" sx={{ py: 1 }}>
                ข้อมูลเบื้องต้น
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", gap: 2 }}>
                {personalInfo.imageUrl && (
                  <img
                    src={personalInfo.imageUrl}
                    alt="รูปภาพผู้สมัคร"
                    style={{ height: "300px" }}
                  />
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    ชื่อ-นามสกุล:
                  </Typography>
                  <Typography level="body-md">
                    {personalInfo.name || "ไม่ระบุ"}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    เลขบัตรประจำตัวประชาชน:
                  </Typography>
                  <Typography level="body-md">
                    {personalInfo.citizenId || "ไม่ระบุ"}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    เบอร์โทรศัพท์:
                  </Typography>
                  <Typography level="body-md">
                    {personalInfo.phone || "ไม่ระบุ"}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    อีเมล:
                  </Typography>
                  <Typography level="body-md">
                    {personalInfo.email || "ไม่ระบุ"}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    วันเกิด:
                  </Typography>
                  <Typography level="body-md">
                    {personalInfo.birthDate || "ไม่ระบุ"}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="body-sm" sx={{ fontWeight: "medium" }}>
                    เพศ:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {personalInfo.sex && (
                      <Chip variant="soft" color="primary" size="sm">
                        เพศกำเนิด: {personalInfo.sex}
                      </Chip>
                    )}
                    {personalInfo.gender && (
                      <Chip variant="soft" color="neutral" size="sm">
                        เพศวิถี: {personalInfo.gender}
                      </Chip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Full Application Data */}
        {candidateData.mp101?.map((mp101) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  ข้อมูลใบสมัคร MP101 แบบเต็ม
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  ส่งเมื่อ{" "}
                  {new Date(mp101.created_at.value).toLocaleString("th-TH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(mp101)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Full Application Data */}
        {candidateData.mp101partylist?.map((mp101) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  ข้อมูลใบสมัคร PL101 แบบเต็ม
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  ส่งเมื่อ{" "}
                  {new Date(mp101.created_at.value).toLocaleString("th-TH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(mp101)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* PC101 Information */}
        {candidateData.pc101?.map((pc101) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  ข้อมูลใบสมัคร PC101
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  ส่งเมื่อ{" "}
                  {pc101.created_at
                    ? new Date(pc101.created_at.value).toLocaleString("th-TH", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "-"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(pc101)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* PC101 Scores */}
        {candidateData.pc101Scores?.map((pc101Scores) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  ข้อมูลคะแนน PC101
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {renderPC101Scores(pc101Scores)}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* PC101 Scores */}
        {candidateData.fieldActivities?.map((fieldActivities) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  บันทึกการทดลองทำงานพื้นที่
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  บันทึกโดย{" "}
                  {
                    fieldActivities?.data.find(
                      (a) =>
                        a.key === "ชื่อ-นามสกุล (ชื่อเล่น) ของผู้กรอกข้อมูล"
                    )?.value
                  }
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {renderPC101Scores(fieldActivities)}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* MP101 Homework Scores */}
        {candidateData.mp101HomeworkScores?.map((mp101HomeworkScores) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  คะแนนการบ้าน
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  บันทึกโดย {mp101HomeworkScores?.answers["47"]?.answer}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(mp101HomeworkScores)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* MP101 Homework Scores */}
        {candidateData.mp101InterviewScores?.map((mp101InterviewScores) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  คะแนนสัมภาษณ์ MP101
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  บันทึกโดย {mp101InterviewScores?.answers["33"]?.answer}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(mp101InterviewScores)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* PL101 Interview Scores */}
        {candidateData.pl101InterviewScores?.map((pl101InterviewScores) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  คะแนนสัมภาษณ์ PL101
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  บันทึกโดย {pl101InterviewScores?.answers["33"]?.answer}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(pl101InterviewScores)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* MP101 Homework Scores */}
        {candidateData.mp101CommitteeComments?.map((committeeComments) => (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  บันทึกความเห็นคณะกรรมการ
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  บันทึกโดย {committeeComments?.answers["8"]?.answer}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>{renderFormElements(committeeComments)}</Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Kaitom Data */}
        {candidateData.kaitomData && candidateData.kaitomData.length > 0 && (
          <Accordion>
            <AccordionSummary>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography level="h3" sx={{ py: 1 }}>
                  บันทึกการลงพื้นที่บนระบบไข่ต้ม
                </Typography>
                <Typography level="body-sm" sx={{ py: 1 }}>
                  ({candidateData.kaitomData.length} รายการ)
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {renderKaitomData(candidateData.kaitomData)}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Scoring Section */}
        {candidateData.mp101 && candidateData.mp101.length > 0 && (
          <Card sx={{ p: 2, mt: 2 }}>
            <Typography level="h3">การประเมิน MP101</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                component="a"
                href={`https://www.jotform.com/252261408422045?typeA=${
                  personalInfo?.citizenId || ""
                }&input9[first]=${personalInfo?.firstName || ""}&input9[last]=${
                  personalInfo?.lastName || ""
                }`}
                target="_blank"
                startDecorator={<EditNoteIcon />}
                color="danger"
                variant="solid"
                size="lg"
              >
                กรอกคะแนนการบ้าน
              </Button>
              <Button
                component="a"
                href={`https://www.jotform.com/252292219035049?typeA=${
                  personalInfo?.citizenId || ""
                }&input35[first]=${
                  personalInfo?.firstName || ""
                }&input35[last]=${personalInfo?.lastName || ""}&email=${
                  user?.email || ""
                }`}
                target="_blank"
                startDecorator={<ForumIcon />}
                color="success"
                variant="solid"
                size="lg"
              >
                กรอกคะแนนสัมภาษณ์
              </Button>
              <Button
                component="a"
                href={`https://www.jotform.com/252321445078050?typeA=${
                  personalInfo?.citizenId || ""
                }&input27[first]=${
                  personalInfo?.firstName || ""
                }&input27[last]=${personalInfo?.lastName || ""}`}
                target="_blank"
                startDecorator={<PsychologyAltIcon />}
                color="warning"
                variant="solid"
                size="lg"
              >
                กรอกบันทึกความเห็นคณะกรรมการ
              </Button>
            </Stack>
          </Card>
        )}
        {candidateData.mp101partylist &&
          candidateData.mp101partylist.length > 0 && (
            <Card sx={{ p: 2, mt: 2 }}>
              <Typography level="h3">การประเมิน PL101</Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  component="a"
                  href={`https://www.jotform.com/253201038999462?typeA=${
                    personalInfo?.citizenId || ""
                  }&input35[first]=${
                    personalInfo?.firstName || ""
                  }&input35[last]=${personalInfo?.lastName || ""}&email=${
                    user?.email || ""
                  }`}
                  target="_blank"
                  startDecorator={<ForumIcon />}
                  color="success"
                  variant="solid"
                  size="lg"
                >
                  กรอกคะแนนสัมภาษณ์
                </Button>
              </Stack>
            </Card>
          )}
      </AccordionGroup>
    </Box>
  );
};

export default CandidateDetailPage;
